import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { colors } from "utils";
import { logError } from "utils/sentryLogger";
import { encode } from "gpt-tokenizer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useAvailableModels } from "hooks/useAvailableModels";
import { DateFilterDropdown, SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { Tab, TabList } from "components/shared/PageTabs/PageTabs";
import {
  FilterBar, FilterGroup, FilterLabel, GenerateBar, GenerateButton, TuningSecondaryBtn,
  TuningTextarea,
} from "components/shared/LlmTuning/LlmTuning";
import {
  JobFailed, errorMessage, getClassrooms, getDefaults, getLabels, getLessonGroups, getRunMetrics, pollJob,
  submitPacket, submitRun,
} from "./api";
import { deleteExperiment, listExperiments, saveExperiment } from "./storage";
import {
  downloadFile, exportMarkdown, exportVariantMarkdown, hydrate, metricsText, packetOf, runOf,
} from "./exports";
import {
  Card, CollapsibleField, CopyButton, DocNote, DocTerm, ErrorText, LinkButton, ManifestGrid, OutputPane,
  Muted, Pre, Row, SectionTitle, VariantEditor,
} from "./views";

const LABELS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const MAX_VARIANTS = 4;

const draftFromDefaults = (defaults, label) => ({
  label,
  provider: defaults.provider,
  model_name: defaults.model_name,
  temperature: defaults.temperature,
  thinking_level: "",
  system_prompt: defaults.freeform_system_prompt,
  user_prompt: defaults.freeform_user_prompt,
  input_toggles: defaults.default_sections,
  // New variants start free-form; saved ones without a mode stay structured.
  output_mode: "freeform",
});

// Editors hold strings; the API wants null for "server default" (for the task
// prompt the default is empty, so a blank Prompt sends no task text).
const toVariant = (draft) => ({
  ...draft,
  model_name: draft.model_name || null,
  temperature: draft.temperature === "" || draft.temperature == null ? null : Number(draft.temperature),
  thinking_level: draft.thinking_level || null,
  system_prompt: draft.system_prompt || null,
  user_prompt: draft.user_prompt || null,
  output_mode: draft.output_mode || "structured",
});

const SAVE_DELAY_MS = 400;

const RUN_STATUS_TEXT = { pending: "Running…", failed: "Failed", expired: "Expired", completed: "" };
const PASS_COLOR = colors.green600;
const FAIL_COLOR = "#B23A2E";

const groupLabel = (g) =>
  g && `${g.title}${g.classroom_name ? ` · ${g.classroom_name}` : ""} · ${g.session_count} sessions`;

const REPORT_TYPES = [
  { value: "lesson_group", label: "By lesson group" },
  { value: "date", label: "By date" },
];

// Local midnight: a bare YYYY-MM-DD parses as UTC and shows the day before in the US.
const shortDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const dateTitle =({ start, mode }, className) =>
  `${mode === "weekly" ? "Week of " : ""}${shortDate(start)}${className ? ` · ${className}` : ""}`;

const METRICS_POLL_MS = 10000;
const METRICS_TRIES = 6;
const FINAL_METRICS = new Set(["ready", "unavailable"]);
// Langfuse still ingesting or rate-limited, or the network dropped: worth another try.
const retryable = (e) => e?.status == null || e.status === 429 || e.status === 503;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Null when the run has no trace or Langfuse isn't configured; otherwise the
// metrics, why they stopped (`note`), or "metrics pending" while polling.
const runMetricsLine = (run, entry, note) => {
  if (!run?.trace_id || entry.metrics?.status === "unavailable") return null;
  return metricsText(entry.metrics) || note || "metrics pending";
};

const JobStatus = ({ job }) => {
  if (job.status === "pending" && job.error) {
    return <ErrorText>Stopped checking ({job.error}). Close and reopen the experiment to resume.</ErrorText>;
  }
  if (job.status === "pending") return <Muted>Running…</Muted>;
  if (job.status === "expired") return <ErrorText>Expired on the server before it was saved.</ErrorText>;
  if (job.status === "failed") return <ErrorText>{job.error}</ErrorText>;
  return null;
};

export const LessonReportConsole = ({ selectedEducator }) => {
  const educatorId = selectedEducator.id;
  const [defaults, setDefaults] = useState(null);
  const [groups, setGroups] = useState(null);
  const [assignmentId, setAssignmentId] = useState("");
  const [reportType, setReportType] = useState("lesson_group");
  const [classrooms, setClassrooms] = useState(null);
  const [classroomsError, setClassroomsError] = useState(false);
  const [classroomId, setClassroomId] = useState("");
  // { start, end (exclusive), mode: "daily" | "weekly" }
  const [range, setRange] = useState(null);
  const [saved, setSaved] = useState([]);
  const [experiment, setExperiment] = useState(null);
  // Experiments opened this session, shown as tabs.
  const [openTabs, setOpenTabs] = useState([]);
  const [labelMap, setLabelMap] = useState(null);
  const [namesUnavailable, setNamesUnavailable] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(null);
  // Which run cards are expanded; screen-only, so reopened experiments start closed.
  const [openRuns, setOpenRuns] = useState({});
  // Why a run's metrics stopped loading, by run job id; screen-only.
  const [metricsNotes, setMetricsNotes] = useState({});
  const { modelOptions, modelsLoading } = useAvailableModels();
  const alive = useRef(true);
  const openId = useRef(null);
  const polling = useRef(new Set());
  const metricsPolling = useRef(new Set());
  // Bumped on every open, so a metrics poll from an earlier visit stops.
  const openGen = useRef(0);
  const unsaved = useRef(null);

  const fail = (e) => setError(errorMessage(e));

  // Disables submit buttons while a POST is in flight: each one is a paid job.
  const submitting = (fn) => async (...args) => {
    setBusy(true);
    try {
      await fn(...args);
    } finally {
      setBusy(false);
    }
  };

  const refreshSaved = useCallback(() => {
    listExperiments()
      .then((all) => setSaved(all
        .filter((e) => e.educator_id === educatorId)
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at))))
      .catch((e) => setError(errorMessage(e)));
  }, [educatorId]);

  // Set on every mount: a remount (e.g. fast refresh) runs the cleanup first, and a
  // flag left false would silently cancel every poll that follows.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    const onError = (e) => setError(errorMessage(e));
    getDefaults().then(setDefaults).catch(onError);
    getLessonGroups(educatorId).then(setGroups).catch(onError);
    refreshSaved();
  }, [educatorId, refreshSaved]);

  useEffect(() => {
    if (reportType !== "date" || classrooms || classroomsError) return;
    getClassrooms(educatorId).then(setClassrooms).catch((e) => {
      setClassroomsError(true);
      setError(errorMessage(e));
    });
  }, [reportType, classrooms, classroomsError, educatorId]);

  const docs = useMemo(
    () => Object.fromEntries((defaults?.section_docs || []).map((d) => [d.key, d])),
    [defaults],
  );

  const flushSave = useCallback(() => {
    const pending = unsaved.current;
    unsaved.current = null;
    return pending ? saveExperiment(pending).catch((e) => setError(errorMessage(e))) : Promise.resolve();
  }, []);

  // Saves once typing pauses; switching, closing and unmounting flush first.
  useEffect(() => {
    if (!experiment) return undefined;
    unsaved.current = experiment;
    const timer = setTimeout(flushSave, SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [experiment, flushSave]);

  useEffect(() => () => { flushSave(); }, [flushSave]);

  // A poll stops when its experiment is closed, switched away from or unmounted;
  // reopening resumes it. A job already being polled is not polled twice.
  const track = useCallback((experimentId, jobId) => {
    if (polling.current.has(jobId)) return;
    polling.current.add(jobId);
    const cancelled = () => !alive.current || openId.current !== experimentId;
    pollJob(jobId, cancelled)
      .then((result) => ({ status: "completed", result }))
      .catch((e) => {
        if (e instanceof JobFailed) return { status: "failed", error: e.message };
        if (e?.status === 404) return { status: "expired", error: errorMessage(e) };
        return { status: "pending", error: errorMessage(e) };
      })
      .then((job) => {
        polling.current.delete(jobId);
        if (cancelled()) return;
        setExperiment((prev) => (prev?.jobs[jobId] ? { ...prev, jobs: { ...prev.jobs, [jobId]: job } } : prev));
      });
  }, []);

  // Langfuse ingests a trace some seconds after the run, so this polls up to
  // METRICS_TRIES times. Only a final status is saved: reopening skips finished
  // metrics and retries the rest. Why polling stopped is shown, never saved.
  const trackMetrics = useCallback((experimentId, jobId, traceId) => {
    if (metricsPolling.current.has(jobId)) return;
    metricsPolling.current.add(jobId);
    const gen = openGen.current;
    const cancelled = () => !alive.current || openId.current !== experimentId || openGen.current !== gen;
    const stop = (note, e, context) => {
      if (cancelled()) return;
      if (e !== undefined) {
        logError("Lesson report console: run metrics failed", e, { traceId, status: e?.status, ...context });
      }
      setMetricsNotes((notes) => ({ ...notes, [jobId]: note }));
    };
    (async () => {
      for (let i = 0; i < METRICS_TRIES; i += 1) {
        if (i) await sleep(METRICS_POLL_MS);
        if (cancelled()) return;
        let metrics;
        try {
          metrics = await getRunMetrics(traceId);
        } catch (e) {
          if (retryable(e)) continue;
          return stop(`metrics unavailable (${errorMessage(e)})`, e);
        }
        if (cancelled()) return;
        if (FINAL_METRICS.has(metrics?.status)) {
          setExperiment((prev) => (prev?.id === experimentId
            ? { ...prev, runs: prev.runs.map((r) => (r.job_id === jobId ? { ...r, metrics } : r)) }
            : prev));
          return;
        }
        if (metrics?.status !== "pending") {
          return stop("metrics unavailable (unexpected response)", null, { received: metrics?.status });
        }
      }
      stop("metrics not ready yet; reopen the experiment to retry");
    })();
  }, []);

  useEffect(() => {
    if (!experiment) return;
    experiment.runs.forEach((entry) => {
      const traceId = runOf(experiment, entry.job_id)?.trace_id;
      if (traceId && !FINAL_METRICS.has(entry.metrics?.status)) trackMetrics(experiment.id, entry.job_id, traceId);
    });
  }, [experiment, trackMetrics]);

  const open = (exp) => {
    flushSave();
    openId.current = exp?.id || null;
    openGen.current += 1;
    metricsPolling.current.clear();
    setMetricsNotes({});
    setLabelMap(null);
    setNamesUnavailable(null);
    setOpenRuns({});
    if (!exp) {
      setExperiment(null);
      return refreshSaved();
    }
    setOpenTabs((tabs) => (tabs.some((t) => t.id === exp.id) ? tabs : [...tabs, { id: exp.id, title: exp.title }]));
    // Only the packet and runs are shown; older experiments may hold other job kinds.
    const shown = [exp.packet_job_id, ...exp.runs.map((r) => r.job_id)];
    const pending = shown.filter((id) => exp.jobs[id]?.status === "pending");
    const jobs = { ...exp.jobs };
    pending.forEach((id) => { jobs[id] = { status: "pending" }; });
    setExperiment({ ...exp, jobs });
    pending.forEach((id) => track(exp.id, id));
  };

  const addJobs = (patch, jobIds) => {
    const expId = experiment.id;
    setExperiment((prev) => {
      if (prev?.id !== expId) return prev;
      const next = patch(prev);
      const jobs = { ...next.jobs };
      jobIds.forEach((id) => { jobs[id] = { status: "pending" }; });
      return { ...next, jobs };
    });
    jobIds.forEach((id) => track(expId, id));
  };

  const buildPacket = submitting(async () => {
    setError(null);
    try {
      const byDate = reportType === "date";
      const scope = byDate
        ? { classroom_id: classroomId, start_date: range.start, end_date: range.end }
        : { assignment_id: assignmentId };
      // Everything that could throw runs before the paid job is queued.
      const title = byDate
        ? dateTitle(range, classrooms.find((c) => c.classroom_id === classroomId)?.name)
        : groups.find((g) => g.assignment_id === assignmentId)?.title || "Lesson";
      const variants = [draftFromDefaults(defaults, "A")];
      const jobId = await submitPacket(educatorId, scope);
      const exp = {
        id: crypto.randomUUID(),
        educator_id: educatorId,
        report_type: reportType,
        ...scope,
        ...(byDate && { date_mode: range.mode }),
        title,
        created_at: new Date().toISOString(),
        packet_job_id: jobId,
        variants,
        runs: [],
        jobs: { [jobId]: { status: "pending" } },
      };
      open(exp);
    } catch (e) {
      fail(e);
    }
  });

  const packet = packetOf(experiment);
  const sectionTokens = useMemo(
    () => Object.fromEntries((packet?.sections || []).map((s) => [s.key, encode(s.content).length])),
    [packet],
  );
  const variants = experiment?.variants;
  const promptTokens = useMemo(
    () => Object.fromEntries((variants || []).map((v) => [
      v.label, encode(`${v.system_prompt || ""}\n${v.user_prompt || ""}`.trim()).length,
    ])),
    [variants],
  );
  const tokenRowsFor = (v) => [
    { label: "Packet sections", tokens: v.input_toggles.reduce((sum, key) => sum + (sectionTokens[key] || 0), 0) },
    { label: "Prompt", tokens: promptTokens[v.label] || 0 },
  ];
  const lastRunOf = (label) => [...(experiment?.runs || [])].reverse().find((r) => r.label === label);
  const isStale = (draft) => {
    const last = lastRunOf(draft.label);
    return Boolean(last) && JSON.stringify(toVariant(last.variant)) !== JSON.stringify(toVariant(draft));
  };
  const h = (text) => hydrate(text, labelMap);

  const setVariants = (variants) => setExperiment((prev) => ({ ...prev, variants }));
  // A label with past runs stays reserved, so a new variant never inherits them.
  const nextLabel = () => LABELS.find(
    (l) => !experiment.variants.some((v) => v.label === l) && !experiment.runs.some((r) => r.label === l),
  );

  // Runs one variant only; earlier runs, including this label's, are kept.
  const runVariant = submitting(async (draft) => {
    setError(null);
    setStarting(draft.label);
    try {
      const sent = toVariant(draft);
      const jobId = await submitRun(packet, sent);
      const entry = { job_id: jobId, label: sent.label, variant: sent, rating: null };
      addJobs((prev) => ({ ...prev, runs: [...prev.runs, entry] }), [jobId]);
      setOpenRuns((o) => ({ ...o, [jobId]: true }));
    } catch (e) {
      fail(e);
    } finally {
      setStarting(null);
    }
  });

  const rate = (runJobId, rating) => setExperiment((prev) => ({
    ...prev,
    runs: prev.runs.map((r) => (r.job_id === runJobId ? { ...r, rating: { ...r.rating, ...rating } } : r)),
  }));

  const toggleNames = async (on) => {
    setNamesUnavailable(null);
    if (!on) return setLabelMap(null);
    const expId = experiment.id;
    try {
      const labels = await getLabels(packet, experiment);
      if (openId.current === expId) setLabelMap(Object.fromEntries(labels.map((l) => [l.label, l.name])));
    } catch (e) {
      // 409: the packet no longer matches its lesson group or class; 503: names can't be
      // loaded right now. Either way labels stay on screen.
      if (e?.status === 409 || e?.status === 503) setNamesUnavailable(errorMessage(e));
      else fail(e);
    }
  };

  const remove = async (exp) => {
    if (!window.confirm(`Delete experiment "${exp.title}"? This cannot be undone.`)) return;
    try {
      await deleteExperiment(exp.id);
    } catch (e) {
      return fail(e);
    }
    setOpenTabs((tabs) => tabs.filter((t) => t.id !== exp.id));
    if (experiment?.id === exp.id) open(null);
    else refreshSaved();
  };

  // Reads the stored copy: the open experiment is saved on every change.
  const switchTo = async (id) => {
    if (busy || id === experiment?.id) return;
    if (!id) return open(null);
    try {
      await flushSave();
      const stored = (await listExperiments()).find((e) => e.id === id);
      if (stored) open(stored);
      else {
        setOpenTabs((tabs) => tabs.filter((t) => t.id !== id));
        setError("That experiment is no longer saved in this browser.");
      }
    } catch (e) {
      fail(e);
    }
  };

  const closeTab = (id) => {
    if (busy) return;
    setOpenTabs((tabs) => tabs.filter((t) => t.id !== id));
    if (id === experiment?.id) open(null);
  };

  const download = (filename, build) => {
    try {
      downloadFile(filename, build(), "text/markdown");
    } catch (e) {
      fail(e);
    }
  };

  const fileStem = experiment && `lesson-report-${experiment.title.replace(/\W+/g, "-")}`;

  return (
    <Wrapper>
      <TabList as="nav" aria-label="Experiments">
        <Tab type="button" aria-current={!experiment ? "page" : undefined} $active={!experiment} disabled={busy}
          onClick={() => switchTo(null)}>
          Experiments
        </Tab>
        {openTabs.map((t) => (
          <TabItem key={t.id}>
            <Tab type="button" aria-current={experiment?.id === t.id ? "page" : undefined}
              $active={experiment?.id === t.id} disabled={busy} onClick={() => switchTo(t.id)}>
              {t.title}
            </Tab>
            <CloseTab type="button" aria-label={`Close ${t.title}`} disabled={busy} onClick={() => closeTab(t.id)}>
              ×
            </CloseTab>
          </TabItem>
        ))}
      </TabList>

      {error && <Card><ErrorText role="alert">{error}</ErrorText></Card>}

      {!experiment && (
        <>
          <Card>
            <SectionTitle as="h2">New experiment</SectionTitle>
            <Muted>Nothing is saved on the server; experiments live in this browser only.</Muted>
            <FilterBar>
              <FilterGroup>
                <SelectDropdown
                  label="Report type"
                  value={reportType}
                  displayText={REPORT_TYPES.find((t) => t.value === reportType).label}
                  options={REPORT_TYPES}
                  onChange={setReportType}
                />
              </FilterGroup>
              {reportType === "lesson_group" && (
                <FilterGroup>
                  <SelectDropdown
                    label="Lesson group"
                    value={assignmentId}
                    displayText={groupLabel(groups?.find((g) => g.assignment_id === assignmentId))
                      || (groups ? "Select a lesson group" : "Loading…")}
                    options={(groups || []).map((g) => ({
                      value: g.assignment_id, label: groupLabel(g), disabled: g.session_count === 0,
                    }))}
                    onChange={setAssignmentId}
                  />
                </FilterGroup>
              )}
              {reportType === "date" && (
                <>
                  <FilterGroup>
                    <SelectDropdown
                      label="Class"
                      value={classroomId}
                      displayText={classrooms?.find((c) => c.classroom_id === classroomId)?.name
                        || (classrooms ? "Select a class" : classroomsError ? "Couldn't load classes" : "Loading…")}
                      options={(classrooms || []).map((c) => ({ value: c.classroom_id, label: c.name }))}
                      onChange={(id) => { setClassroomId(id); setRange(null); }}
                    />
                  </FilterGroup>
                  {classroomId && (
                    <FilterGroup>
                      <DateFilterDropdown
                        label="Day or week"
                        classroomId={classroomId}
                        educatorId={educatorId}
                        startDate={range?.start}
                        endDate={range?.end}
                        selectedDateMode={range?.mode}
                        hideCustomRange
                        onDateChange={(start, end, mode) => setRange({ start, end, mode })}
                      />
                    </FilterGroup>
                  )}
                </>
              )}
            </FilterBar>
            <GenerateBar>
              <GenerateButton type="button" onClick={buildPacket}
                disabled={busy || !defaults || (reportType === "date" ? !(classroomId && range) : !assignmentId)}>
                {busy ? <><FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: "8px" }} />Building packet...</> : "Build packet"}
              </GenerateButton>
            </GenerateBar>
            {reportType === "lesson_group" && groups?.length === 0 && (
              <Muted>No lesson groups assigned in the last 90 days.</Muted>
            )}
            {reportType === "date" && classrooms?.length === 0 && <Muted>This teacher has no classes.</Muted>}
          </Card>

          <Card>
            <SectionTitle as="h2">Saved experiments</SectionTitle>
            {saved.length === 0 && <Muted>None yet for this teacher.</Muted>}
            {saved.map((exp) => (
              <Row key={exp.id}>
                <span>{exp.title}</span>
                <Muted>{new Date(exp.created_at).toLocaleString()} · {exp.runs.length} runs</Muted>
                <LinkButton type="button" disabled={busy} onClick={() => open(exp)}>
                  {openTabs.some((t) => t.id === exp.id) ? "Go to tab" : "Open"}
                </LinkButton>
                <LinkButton type="button" onClick={() => remove(exp)}>Delete</LinkButton>
              </Row>
            ))}
          </Card>
        </>
      )}

      {experiment && (
        <>
          <Card>
            <Row>
              <SectionTitle as="h2">{experiment.title}</SectionTitle>
              <LinkButton type="button" disabled={busy} onClick={() => closeTab(experiment.id)}>Close</LinkButton>
              {packet && (
                <label>
                  <input type="checkbox" checked={!!labelMap} onChange={(e) => toggleNames(e.target.checked)} />
                  {" "}Show real names (on screen only)
                </label>
              )}
              {namesUnavailable && <Muted>Real names unavailable ({namesUnavailable}).</Muted>}
            </Row>
          </Card>

          <Card>
            <SectionTitle>Packet</SectionTitle>
            <JobStatus job={experiment.jobs[experiment.packet_job_id]} />
            {experiment.jobs[experiment.packet_job_id].status === "failed" && (
              <Muted>If no names could be resolved, the privacy guard refused the packet; retry later.</Muted>
            )}
            {packet && (
              <>
                <Muted>
                  Built {new Date(packet.built_at).toLocaleString()} · ~{packet.token_estimate} tokens
                  · standards {packet.standard_codes.join(", ") || "—"}
                </Muted>
                <Muted>Students: {packet.student_labels.map(h).join(", ")}</Muted>
                {packet.groups?.length > 0 && (
                  <Muted>
                    Groups: {packet.groups.map((g) => `${g.label} (${g.session_labels.length})`).join(" · ")}
                  </Muted>
                )}
                <ManifestGrid manifest={packet.manifest} docs={docs} />
                {packet.sections.map((s) => (
                  <details key={s.key}>
                    <summary aria-describedby={docs[s.key] ? `packet-doc-note-${s.key}` : undefined}>
                      <DocTerm id={`packet-doc-${s.key}`} doc={docs[s.key]} label={s.title} />
                      {" "}<Muted>({s.key})</Muted>
                    </summary>
                    <DocNote id={`packet-doc-note-${s.key}`} doc={docs[s.key]} />
                    <CopyButton text={s.content} label={`Copy ${s.title}`} />
                    <Pre>{h(s.content)}</Pre>
                  </details>
                ))}
              </>
            )}
          </Card>

          {packet && defaults && (
            <>
              <SectionTitle>Variants</SectionTitle>
              {experiment.variants.map((v, i) => (
                <VariantEditor
                  key={v.label}
                  variant={v}
                  sectionKeys={defaults.section_keys}
                  defaults={defaults}
                  modelOptions={modelOptions}
                  modelsLoading={modelsLoading}
                  tokenRows={tokenRowsFor(v)}
                  runLabel={`${lastRunOf(v.label) ? "Re-run" : "Run"} variant ${v.label}`}
                  running={starting === v.label}
                  runDisabled={busy}
                  stale={isStale(v)}
                  namesShown={!!labelMap}
                  docs={docs}
                  onRun={() => runVariant(v)}
                  onChange={(next) => setVariants(experiment.variants.map((x, j) => (j === i ? next : x)))}
                  onRemove={experiment.variants.length > 1
                    ? () => setVariants(experiment.variants.filter((_, j) => j !== i))
                    : null}
                  onExport={() => download(
                    `${fileStem}-variant-${v.label}.md`, () => exportVariantMarkdown(experiment, v.label),
                  )}
                  onDuplicate={experiment.variants.length < MAX_VARIANTS
                    ? () => setVariants([...experiment.variants, { ...v, label: nextLabel() }])
                    : null}
                />
              ))}
              <GenerateBar>
                <Row>
                  <TuningSecondaryBtn type="button" disabled={experiment.variants.length >= MAX_VARIANTS}
                    onClick={() => setVariants([...experiment.variants, draftFromDefaults(defaults, nextLabel())])}>
                    + Add variant
                  </TuningSecondaryBtn>
                </Row>
              </GenerateBar>
            </>
          )}

          {experiment.runs.length > 0 && <SectionTitle>Results</SectionTitle>}
          {experiment.runs.map((entry, i) => {
            const run = runOf(experiment, entry.job_id);
            const job = experiment.jobs[entry.job_id];
            const verdict = entry.rating?.verdict;
            const isOpen = !!openRuns[entry.job_id];
            const metricsLine = runMetricsLine(run, entry, metricsNotes[entry.job_id]);
            return (
              <RunCard key={entry.job_id} $verdict={verdict}>
                <RunHeader type="button" aria-expanded={isOpen} aria-controls={`run-body-${entry.job_id}`}
                  onClick={() => setOpenRuns((o) => ({ ...o, [entry.job_id]: !isOpen }))}>
                  <span>
                    <SectionTitle as="span">Run {i + 1}: variant {entry.label}</SectionTitle>
                    {" "}
                    <Muted>
                      {run
                        ? `${run.provider} / ${run.model_name} · temp ${run.temperature}`
                          + `${run.thinking_level ? ` · thinking ${run.thinking_level}` : ""}`
                          + `${run.output_mode === "freeform" ? " · freeform" : ""}`
                          + ` · ${run.latency_ms} ms · prompt ${run.prompt_version}`
                          + `${metricsLine ? ` · ${metricsLine}` : ""}`
                        : RUN_STATUS_TEXT[job.status]}
                    </Muted>
                  </span>
                  <span>
                    {verdict && <VerdictTag $verdict={verdict}>{verdict === "pass" ? "✓ pass" : "✗ fail"}</VerdictTag>}
                    <FontAwesomeIcon icon={faChevronDown}
                      style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                  </span>
                </RunHeader>
                {isOpen && (
                <RunBody id={`run-body-${entry.job_id}`}>
                <JobStatus job={job} />
                {run && (
                  <>
                    <OutputPane run={run} h={h} />
                    {run.output && (
                      <CollapsibleField label="Model scratchpad" hint="the model's planning notes">
                        <ul>{(run.output._local_scratchpad || []).map((s, k) => <li key={k}>{h(s)}</li>)}</ul>
                      </CollapsibleField>
                    )}
                    <CollapsibleField label="Exact prompt sent" hint="labels only, as the model received it">
                      <Muted as="div">System prompt</Muted>
                      <Pre>{run.system_prompt}</Pre>
                      <Muted as="div">User message</Muted>
                      <Pre>{run.user_message}</Pre>
                    </CollapsibleField>
                    <Row>
                      <LinkButton type="button"
                        onClick={() => download(`${fileStem}-run${i + 1}.md`, () => exportMarkdown(experiment, entry))}>
                        Export Markdown
                      </LinkButton>
                      {entry.metrics?.trace_url && (
                        <a href={entry.metrics.trace_url} target="_blank" rel="noopener noreferrer">
                          Open trace in Langfuse
                        </a>
                      )}
                    </Row>
                    <Row>
                      <span>Your call:</span>
                      {["pass", "fail"].map((verdict) => (
                        <label key={verdict}>
                          <input type="radio" name={`rating-${entry.job_id}`} checked={entry.rating?.verdict === verdict}
                            onChange={() => rate(entry.job_id, { verdict })} />
                          {" "}{verdict}
                        </label>
                      ))}
                    </Row>
                    <FilterLabel htmlFor={`rating-note-${entry.job_id}`}>
                      Why?{" "}
                      {labelMap && <Muted>(hide real names to edit; notes are saved and exported)</Muted>}
                    </FilterLabel>
                    <TuningTextarea
                      id={`rating-note-${entry.job_id}`}
                      rows={2}
                      placeholder="What made this report pass or fail? Use student labels, not names."
                      value={entry.rating?.note || ""}
                      readOnly={!!labelMap}
                      onChange={(e) => rate(entry.job_id, { note: e.target.value })}
                    />
                  </>
                )}
                </RunBody>
                )}
              </RunCard>
            );
          })}

        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
`;

const TabItem = styled.div`
  display: flex;
  align-items: center;
`;

const CloseTab = styled.button`
  border: none;
  background: none;
  color: ${colors.milkGlass950};
  font-size: 18px;
  line-height: 1;
  padding: 4px 8px;
  margin-left: -12px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    color: ${colors.nightlyWoods500};
    background: ${colors.milkGlass200};
  }
`;

const verdictColor = ({ $verdict }) =>
  ($verdict === "pass" ? PASS_COLOR : $verdict === "fail" ? FAIL_COLOR : "transparent");

const RunCard = styled(Card)`
  border: 2px solid ${verdictColor};
  padding: 0;
  gap: 0;
`;

const RunHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;

  > span:last-child {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }
`;

const RunBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px 16px;
`;

const VerdictTag = styled.span`
  color: ${verdictColor};
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 700;
`;
